/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import { Tooltip } from 'radix-ui';
import React, { useState } from 'react';

import { useAccount } from '~/apis';
import { useFirebaseAnalytics } from '~/apis/useFirebase';
import { selectRejoinState, useSelector, useTranslation } from '~/app';
import { config, tutorialConfig } from '~/app/config';
import {
  ROUTE_FREEGAME,
  ROUTE_TABLE,
  ROUTE_TOPLIST,
  ROUTE_TOURNAMENT,
  ROUTE_TRAINING,
} from '~/app/routes';
import { useAuthDispatch, useAuthState } from '~/auth';
import AccountMenuContent, { MenuOption } from '~/components/AccountMenu/AccountMenuContent';
import { ReactComponent as LoginIcon } from '~/components/AccountMenu/login.svg';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/DropdownMenu/DropdownMenu';
import { LanguageSubmenu } from '~/components/LanguageSubmenu';
import { useLogoutDialog } from '~/components/LogoutDialogContext';
import RejoinIcon from '~/components/RejoinIcon';
import TableIcon from '~/components/TableIcon';
import ToplistIcon from '~/components/ToplistIcon';
import azaspieleLogo from '~/overlay/button-icons/azaspiele.png';
import { ReactComponent as InstructionsIcon } from '~/overlay/button-icons/button-instructions.svg';
import { ReactComponent as YouTubeIcon } from '~/overlay/button-icons/button-youtube.svg';
import { ReactComponent as DiscordIcon } from '~/overlay/button-icons/discord.svg';
import { ReactComponent as WhatsAppIcon } from '~/overlay/button-icons/whatsapp.svg';
const HamburgerIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const HeaderOverflowMenu = (): JSX.Element => {
  const { t, urlPath, locale } = useTranslation();
  const navigate = useNavigate();
  const { isAuthorized } = useAuthState();
  const authDispatch = useAuthDispatch();
  const { openLogoutDialog } = useLogoutDialog();
  const analytics = useFirebaseAnalytics();
  const rejoinState = useSelector(selectRejoinState);
  const { isSuccess } = useAccount();

  const [isOpen, setIsOpen] = useState(false);

  const showRejoin =
    (urlPath === '/' ||
      urlPath === '/account' ||
      urlPath === '/account/subscriptions' ||
      urlPath === '/table' ||
      urlPath === '/toplist') &&
    isSuccess &&
    rejoinState.type === 'success';

  const closeMenu = () => setIsOpen(false);

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div className="relative">
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative my-1 flex h-10 transform-gpu cursor-pointer flex-row items-center justify-center rounded-md bg-primary-light px-2 py-2 transition-all hover:scale-[1.02] hover:brightness-125 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden active:scale-[0.98] active:brightness-125 sm:px-3"
                  aria-label={t('button_menuTooltip')}
                >
                  <HamburgerIcon />
                  <span className="hidden pl-2 align-middle text-sm sm:inline">
                    {t('button_menu')}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-[85vh] min-w-[200px] overflow-y-auto"
              >
                {showRejoin && (
                  <>
                    <DropdownMenuItem asChild>
                      <MenuOption
                        icon={<RejoinIcon />}
                        label={
                          rejoinState.mode === 'training'
                            ? t('button_rejoinTraining')
                            : rejoinState.mode === 'free'
                              ? t('button_rejoinFree')
                              : t('button_rejoinTournament')
                        }
                        onClick={() => {
                          if (rejoinState.mode === 'training') {
                            navigate({ to: ROUTE_TRAINING });
                          } else if (rejoinState.mode === 'free') {
                            navigate({ to: ROUTE_FREEGAME });
                          } else if (rejoinState.mode === 'tournament') {
                            navigate({ to: ROUTE_TOURNAMENT });
                          }
                          closeMenu();
                        }}
                        isInDropdown
                      />
                    </DropdownMenuItem>
                  </>
                )}

                <DropdownMenuItem asChild>
                  <MenuOption
                    icon={<TableIcon />}
                    label={t('button_table')}
                    onClick={() => {
                      if (isAuthorized) {
                        navigate({ to: ROUTE_TABLE });
                      } else {
                        authDispatch({ type: 'login' });
                      }
                      closeMenu();
                    }}
                    isInDropdown
                  />
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <MenuOption
                    icon={<ToplistIcon />}
                    label={t('button_toplist')}
                    onClick={() => {
                      if (isAuthorized) {
                        navigate({ to: ROUTE_TOPLIST });
                      } else {
                        authDispatch({ type: 'login' });
                      }
                      closeMenu();
                    }}
                    isInDropdown
                  />
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <MenuOption
                    icon={<InstructionsIcon width={24} height={24} />}
                    label={t('button_instructions')}
                    onClick={() => {
                      const pdfUrl =
                        tutorialConfig.instructionsPdf[
                          locale as keyof typeof tutorialConfig.instructionsPdf
                        ] || tutorialConfig.instructionsPdf.en;
                      window.open(pdfUrl, '_blank');
                      analytics?.logEvent('open_instructions', {
                        event_category: 'tutorial',
                        event_label: 'instructions_pdf',
                        language: locale,
                      });
                      closeMenu();
                    }}
                    isInDropdown
                  />
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <MenuOption
                    icon={<YouTubeIcon width={24} height={24} />}
                    label={t('button_youtube')}
                    onClick={() => {
                      window.open(tutorialConfig.youtubeChannel, '_blank');
                      analytics?.logEvent('open_youtube', {
                        event_category: 'tutorial',
                        event_label: 'youtube_channel',
                      });
                      closeMenu();
                    }}
                    isInDropdown
                  />
                </DropdownMenuItem>

                {config.discordEnabled && config.discordInviteUrl && (
                  <DropdownMenuItem asChild>
                    <MenuOption
                      icon={<DiscordIcon width={24} height={24} />}
                      label={t('button_discord')}
                      onClick={() => {
                        window.open(config.discordInviteUrl, '_blank');
                        analytics?.logEvent('open_discord', {
                          event_category: 'community',
                          event_label: 'discord_community',
                        });
                        closeMenu();
                      }}
                      isInDropdown
                    />
                  </DropdownMenuItem>
                )}

                {config.whatsappEnabled && config.whatsappInviteUrl && (
                  <DropdownMenuItem asChild>
                    <MenuOption
                      icon={<WhatsAppIcon width={24} height={24} />}
                      label={t('button_whatsapp')}
                      onClick={() => {
                        window.open(config.whatsappInviteUrl, '_blank');
                        analytics?.logEvent('open_whatsapp', {
                          event_category: 'community',
                          event_label: 'whatsapp_community',
                        });
                        closeMenu();
                      }}
                      isInDropdown
                    />
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem asChild>
                  <MenuOption
                    icon={
                      <span
                        style={{
                          background: 'white',
                          borderRadius: 4,
                          padding: 2,
                          display: 'inline-block',
                        }}
                      >
                        <img
                          src={azaspieleLogo}
                          alt="AZA-Spiele"
                          style={{
                            height: 20,
                            width: 'auto',
                            display: 'block',
                            objectFit: 'contain',
                          }}
                        />
                      </span>
                    }
                    label={t('button_developer')}
                    onClick={() => {
                      const homepageUrl =
                        tutorialConfig.creatorHomepage[
                          locale as keyof typeof tutorialConfig.creatorHomepage
                        ] || tutorialConfig.creatorHomepage.en;
                      window.open(homepageUrl, '_blank');
                      analytics?.logEvent('open_developer', {
                        event_category: 'tutorial',
                        event_label: 'developer_homepage',
                        language: locale,
                      });
                      closeMenu();
                    }}
                    isInDropdown
                  />
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <LanguageSubmenu />

                <DropdownMenuSeparator />

                {!isAuthorized ? (
                  <DropdownMenuItem asChild>
                    <MenuOption
                      icon={<LoginIcon />}
                      label={t('start_accountLoginButtonText')}
                      onClick={() => {
                        authDispatch({ type: 'login' });
                        closeMenu();
                      }}
                      isInDropdown
                    />
                  </DropdownMenuItem>
                ) : (
                  <AccountMenuContent
                    onClose={closeMenu}
                    onRequestLogout={() => {
                      openLogoutDialog();
                      closeMenu();
                    }}
                    embedInOverflow
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
          >
            {t('button_menuTooltip')}
            <Tooltip.Arrow className="fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default HeaderOverflowMenu;
